import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type UpdateCompanyAccountPayload } from './types';
import { zodUpdateCompanyAccountPayloadSchema } from './validations';

/**
 * Updates a company account.
 * @param companyAccountId - The ID of the company account to update.
 * @param payload - The company account data to update.
 * @returns The updated company account data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/update_company_account_vadmin_company_account__company_account_id__put
 */
export const updateCompanyAccount = async ({
  companyAccountData,
  companyAccountId,
}: {
  companyAccountData: UpdateCompanyAccountPayload;
  companyAccountId: UUID;
}) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.ACCOUNT.UPDATE(companyAccountId), {
        json: companyAccountData,
        validate: {
          body: zodUpdateCompanyAccountPayloadSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company account',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.updateCompanyAccount.error404,
            { ns: 'data' },
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.updateCompanyAccount.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.updateCompanyAccount.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
